import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { loginUser, clearError, sendResetEmail } from '../store/authSlice';
import {config} from '../config'
import logo from '../assets/logo.png'

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/conversation');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      return;
    }
    dispatch(loginUser(formData));
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setResetMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setResetLoading(true);
    setResetMessage(null);

    try {
      await dispatch(sendResetEmail(resetEmail)).unwrap();
      setResetMessage({
        type: 'success',
        text: 'If the email exists in our system, we have sent a password reset link.'
      });
      setResetEmail('');
    } catch (error: any) {
      console.error('Password reset error:', error);

      // For security, don't reveal if email exists or not
      // Only show specific errors for invalid format or rate limiting
      if (error.includes('invalid-email')) {
        setResetMessage({ type: 'error', text: 'Please enter a valid email address' });
      } else if (error.includes('too-many-requests')) {
        setResetMessage({ type: 'error', text: 'Too many requests. Please try again later' });
      } else {
        // For user-not-found and other errors, show generic success message
        // This prevents email enumeration attacks
        setResetMessage({
          type: 'success',
          text: 'If the email exists in our system, we have sent a password reset link.'
        });
        setResetEmail('');
      }
    } finally {
      setResetLoading(false);
    }
  };

  if (showResetPassword) {
    return (
      <Container maxWidth="sm" className="min-h-screen flex items-center justify-center bg-gray-50">
        <Paper elevation={3} className="p-8 w-full">
          <Box className="text-center mb-6">
            <img
              src={logo}
              alt="Logo"
              style={{ maxWidth: '150px', height: 'auto', marginBottom: '16px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Enter your email address and we'll send you a link to reset your password.
            </Typography>
          </Box>

          {resetMessage && (
            <Alert severity={resetMessage.type} className="mb-4">
              {resetMessage.text}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordReset}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              required
              margin="normal"
              autoComplete="email"
              autoFocus
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="mt-6 mb-4"
              disabled={resetLoading || !resetEmail}
              size="large"
            >
              {resetLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Send Reset Email'
              )}
            </Button>

            <Box className="text-center">
              <Button
                onClick={() => {
                  setShowResetPassword(false);
                  setResetMessage(null);
                  setResetEmail('');
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                Back to Sign In
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" className="min-h-screen flex items-center justify-center bg-gray-50">
      <Paper elevation={3} className="p-8 w-full">
        <Box className="text-center mb-6">
          <img
            src={logo}
            alt="Logo"
            style={{ maxWidth: '250px', height: 'auto', marginBottom: '16px', display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          />
          <Typography variant="h4" component="h1" gutterBottom>
            {config.app.name}
          </Typography>
          <Typography variant="h5" component="h2" color="textSecondary">
            Sign In
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            margin="normal"
            autoComplete="email"
            autoFocus
          />
          
          <TextField
            fullWidth
            label="Password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            required
            margin="normal"
            autoComplete="current-password"
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            className="mt-6 mb-4"
            disabled={loading || !formData.email || !formData.password}
            size="large"
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Sign In'
            )}
          </Button>

          <Box className="text-center">
            <Button
              onClick={() => {
                setShowResetPassword(true);
                setResetEmail(formData.email);
              }}
              className="text-blue-600 hover:text-blue-800 mb-2"
              fullWidth
            >
              Forgot Password?
            </Button>
            <Typography variant="body2" color="textSecondary">
              Contact your administrator to create an account
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;